import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Grid,
  Typography,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { z } from 'zod'
import { searchSectionMasterApi } from '../../../../api/SectionMasterApi.js'
import CustomSelect from '../../CustomSelect'
import CustomInput from '../../CustomInput'

const sectionKeywordSchema = z.object({
  id: z.string().nullable().optional(),
  sectionCode: z.string().min(1, 'Section code is required'),
  broadHead: z.string().min(1, 'Broad head is required'),
  keywords: z
    .string()
    .min(1, 'Keywords are required')
    .refine(val => /[a-zA-Z0-9]/.test(val), {
      message: 'Keywords must contain at least one letter or number',
    }),
})

const AddSectionKeyword = ({
  formData,
  formErrors = {},
  formLoading,
  onChange,
  onSubmit,
  onClose,
  tanMasterId,
}) => {
  const [errors, setErrors] = useState(formErrors)
  const [sectionOptions, setSectionOptions] = useState([])
  const [broadHeadOptions, setBroadHeadOptions] = useState([])
  const [allSectionData, setAllSectionData] = useState([])
  const isEditMode = formData?.id && formData.id !== ''

  useEffect(() => {
    setErrors(formErrors)
  }, [formErrors])

  // Fetch dropdown options
  const fetchDropdown = async tanUuid => {
    try {
      const searchRes = await searchSectionMasterApi(
        {
          pageFilter: {
            currentPage: 1,
            includeCount: true,
            pageSize: 100,
            sortField: 'sectionCode',
            sortOrder: 'ASC',
          },
          requestFilter: [],
          isReportDownload: false,
          searchCOList: [],
          hitType: 'GET_SECTION_MASTER',
        },
        tanUuid
      )

      const apiData = searchRes?.data?.content || []

      // Exclude Non-Resident sections per PNW-1282 AC5:
      // Only Resident and Both sections are allowed in Section Keyword determination
      const filteredData = apiData.filter(item => item.applicableTo !== 'Non-Resident')
      setAllSectionData(filteredData)

      // unique section codes from filtered (non-NR) data
      const uniqueSections = [...new Set(filteredData.map(item => item.sectionCode))]
      setSectionOptions(uniqueSections)
    } catch (err) {
      console.error('Error fetching section master:')
    }
  }

  useEffect(() => {
    if (tanMasterId && !isEditMode) {
      fetchDropdown(tanMasterId)
    }
  }, [tanMasterId, isEditMode])

  const handleChange = e => {
    const { name, value } = e.target

    // Clear error for the field being changed
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }

    // Section dropdown change
    if (name === 'sectionCode' && !isEditMode) {
      if (value) {
        const filtered = allSectionData.filter(item => item.sectionCode === value)
        const uniqueBroadHeads = [...new Set(filtered.map(item => item.broadHead))]
        setBroadHeadOptions(uniqueBroadHeads)

        if (uniqueBroadHeads.length === 1) {
          // Auto-select and disable
          onChange({
            ...formData,
            sectionCode: value,
            broadHead: uniqueBroadHeads[0],
            broadHeadDisabled: true
          })
          // Clear Broad Head error
          setErrors(prev => {
            const newErrors = { ...prev }
            delete newErrors.broadHead
            return newErrors
          })
        } else {
          // Multiple options: clear Broad Head and enable
          onChange({
            ...formData,
            sectionCode: value,
            broadHead: '',
            broadHeadDisabled: false
          })
        }
      } else {
        setBroadHeadOptions([])
        onChange({
          ...formData,
          sectionCode: '',
          broadHead: '',
          broadHeadDisabled: false
        })
      }
      return
    }

    // Broad Head dropdown change
    if (name === 'broadHead') {
      // Clear error for Broad Head
      if (errors.broadHead) {
        setErrors(prev => {
          const newErrors = { ...prev }
          delete newErrors.broadHead
          return newErrors
        })
      }
    }

    onChange({ ...formData, [name]: value })
  }


  const handleSubmit = e => {
    e.preventDefault()

    // Add type checking before validation
    if (!formData || typeof formData !== 'object' || Array.isArray(formData)) {
      setErrors({ general: 'Invalid form data. Please refresh and try again.' })
      return
    }

    const result = sectionKeywordSchema.safeParse(formData)
    if (!result.success) {
      const formattedErrors = {}
      result.error.issues.forEach(err => {
        // Handle cases where path might be empty or undefined
        const fieldName = err.path.length > 0 ? err.path[0] : 'general'
        formattedErrors[fieldName] = err.message
      })
      setErrors(formattedErrors)
      return
    }

    setErrors({})
    onSubmit(formData)
  }

  const handleClose = () => {
    setSectionOptions([])
    setBroadHeadOptions([])
    onClose()
  }

  // Add safety check for formData
  if (!formData || typeof formData !== 'object' || Array.isArray(formData)) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">
          Invalid form data. Please refresh and try again.
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        maxWidth: '620px',
        width: '100%',
        mx: 'auto',
        position: 'relative',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          pt: 3,
          pb: 1,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: '#333333',
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          {isEditMode ? 'Edit Keyword' : 'Add Keyword'}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            color: '#666666',
            padding: '4px',
            '&:hover': { backgroundColor: '#f5f5f5' },
          }}
        >
          <CloseIcon sx={{ fontSize: '20px' }} />
        </IconButton>
      </Box>

      {/* Mandatory fields note */}
      <Box sx={{ px: 3, pb: 1 }}>
        <Typography
          variant="body2"
          sx={{
            color: 'var(--forbrand-error)',
            fontSize: '14px',
            fontWeight: 400,
            fontFamily: 'system-ui, -apple-system, sans-serif',
            pb: '24px',
          }}
        >
          (*) Fields are mandatory
        </Typography>
      </Box>

      {/* Form */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ px: 3, pb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}
      >
        {/* Show general error if exists */}
        {errors.general && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {errors.general}
          </Typography>
        )}

        <Grid container spacing={3}>
          {/* Section Code */}
          <Grid item size={6}>
            <CustomSelect
              label="Section Code"
              required
              placeholder="Select Section Code"
              options={isEditMode ? [formData.sectionCode] : sectionOptions}
              value={formData.sectionCode || ''}
              onChange={e => handleChange({ target: { name: 'sectionCode', value: e.target.value } })}
              disabled={isEditMode || sectionOptions.length === 0}
              error={!!errors.sectionCode}
              helperText={errors.sectionCode}
            />
          </Grid>

          {/* Broad Head */}
          <Grid item size={6}>
            <CustomSelect
              label="Broad Head"
              required
              placeholder="Select Broad Head"
              options={isEditMode ? [formData.broadHead] : broadHeadOptions}
              value={formData.broadHead || ''}
              onChange={e => handleChange({ target: { name: 'broadHead', value: e.target.value } })}
              disabled={
                isEditMode ||
                broadHeadOptions.length === 0 ||
                formData.broadHeadDisabled
              }
              error={!!errors.broadHead}
              helperText={errors.broadHead}
            />
          </Grid>

          {/* Keywords */}
          <Grid item size={12}>
            <CustomInput
              label="Keywords"
              required
              placeholder="Enter keywords"
              multiline={true}
              rows={4}
              value={formData.keywords || ''}
              onChange={e => handleChange({ target: { name: 'keywords', value: e.target.value } })}
              error={!!errors.keywords}
              helperText={errors.keywords}
            />
          </Grid>
        </Grid>

        {/* Footer Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: 'var(--forbrand-bg-variant)',
              color: 'var(--forbrand-brand-varient)',
              textTransform: 'none',
              px: 3,
              py: 1,
            }}
            disabled={formLoading}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default AddSectionKeyword
